import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { SignedIn, SignedOut } from "@clerk/clerk-react";
import { api } from "./api";
import type { Case, CaseVolume } from "./types";
import type { CaseViewTab } from "./components/CaseView";
import type { InterrogateScope } from "./components/Interrogate";
import Sidebar from "./components/Sidebar";
import NewCaseForm from "./components/NewCaseForm";
import CaseView from "./components/CaseView";
import Settings from "./components/Settings";
import Guide from "./components/Guide";
import LandingPage from "./components/LandingPage";
import PrivacyPage from "./components/PrivacyPage";
import TermsPage from "./components/TermsPage";
import ChangelogPage from "./components/ChangelogPage";
import GuidesIndexPage from "./components/guides/GuidesIndexPage";
import GuidePage from "./components/guides/GuidePage";
import PublicShareView from "./components/PublicShareView";
import Onboarding from "./components/Onboarding";
import TrialBanner from "./components/TrialBanner";
import {
  applyAppearance,
  getSettings,
  updateSettings,
} from "./lib/settings";
import { useSettings } from "./lib/useSettings";
import { setAiUsageListener } from "./lib/aiUsageSignal";
import type { AiUsageMeter } from "./types";

type View =
  | { kind: "empty" }
  // presetFolder: null = default to Ungrouped, string = preselect that
  //               folder in the dropdown.
  // folderLocked: true when the user entered the form via a folder's
  //               hover-revealed "+" icon, so the dropdown can't be
  //               changed — the context is already established.
  | { kind: "new"; presetFolder: string | null; folderLocked: boolean }
  | { kind: "case"; id: string };

// --- Path routing -----------------------------------------------------------
// The SPA handles these non-app URLs:
//   /welcome           → marketing landing page
//   /privacy           → privacy policy
//   /terms             → terms of service
//   /changelog         → public changelog
//   /guides            → SEO guide index
//   /guides/<slug>     → individual SEO guide
//   /share/<token>     → public read-only case view
// Everything else falls through to the authenticated app at /.

type Route =
  | { kind: "app" }
  | { kind: "landing" }
  | { kind: "privacy" }
  | { kind: "terms" }
  | { kind: "changelog" }
  | { kind: "guides-index" }
  | { kind: "guide"; slug: string }
  | { kind: "share"; token: string };

// casethread.app is the marketing hostname — its root serves the public
// landing page. casethread.pages.dev (and any preview / dev host) keeps
// serving the authenticated app at / unchanged, so the working product
// is never hidden behind marketing for existing users.
function isMarketingHost(hostname: string): boolean {
  return hostname === "casethread.app" || hostname === "www.casethread.app";
}

function routeFromPath(pathname: string): Route {
  // Strip hash + query before route-matching. Footer anchors like
  // `/welcome#about` and utm-tagged links would otherwise fall through
  // the exact-equality checks and end up on the authenticated-app
  // branch, which on casethread.pages.dev would flash-redirect a
  // signed-out visitor straight to /welcome — same destination but
  // via the ugly path.
  const clean = pathname.split("#")[0]!.split("?")[0]!;
  if (clean === "/welcome") return { kind: "landing" };
  if (clean === "/privacy") return { kind: "privacy" };
  if (clean === "/terms") return { kind: "terms" };
  if (clean === "/changelog") return { kind: "changelog" };
  if (clean === "/guides" || clean === "/guides/") {
    return { kind: "guides-index" };
  }
  const guideMatch = clean.match(/^\/guides\/([^/?#]+)\/?$/);
  if (guideMatch) return { kind: "guide", slug: guideMatch[1]! };
  const shareMatch = clean.match(/^\/share\/([^/?#]+)/);
  if (shareMatch) return { kind: "share", token: shareMatch[1]! };
  if (clean === "/" && isMarketingHost(window.location.hostname)) {
    return { kind: "landing" };
  }
  return { kind: "app" };
}

function useRoute(): Route {
  const [route, setRoute] = useState<Route>(() =>
    routeFromPath(window.location.pathname),
  );
  useEffect(() => {
    const onPop = () => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    // Intercept internal anchor clicks (href="/welcome" etc.) so we stay SPA.
    const onClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement | null;
      if (!target) return;
      const anchor = target.closest("a");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (
        !href ||
        !href.startsWith("/") ||
        href.startsWith("//") ||
        anchor.target === "_blank" ||
        e.metaKey ||
        e.ctrlKey
      ) {
        return;
      }
      // Same-page hash links (e.g. /welcome#about from the landing
      // footer while the user is already on /welcome) need native
      // handling so the browser can scroll to the anchor. preventDefault
      // would swallow the hash-change AND the scroll.
      const url = new URL(href, window.location.origin);
      if (url.pathname === window.location.pathname && url.hash) {
        return;
      }
      e.preventDefault();
      window.history.pushState({}, "", href);
      setRoute(routeFromPath(href));
    };
    document.addEventListener("click", onClick);
    return () => {
      window.removeEventListener("popstate", onPop);
      document.removeEventListener("click", onClick);
    };
  }, []);
  return route;
}

const ONBOARDING_KEY = "casethread:onboarding-complete";
// Desktop sidebar collapse is a per-user preference (persisted), distinct from
// the mobile drawer open/close (transient). Collapsed = the nav rail is hidden
// so the main view (e.g. the Financials triage split) reclaims the width.
const SIDEBAR_COLLAPSED_KEY = "casethread:sidebar-collapsed";

function loadSidebarCollapsed(): boolean {
  try {
    return window.localStorage.getItem(SIDEBAR_COLLAPSED_KEY) === "1";
  } catch {
    return false;
  }
}

// Set the client-side robots directive to match the server-side
// X-Robots-Tag (see public/_headers). Marketing routes get
// "index, follow"; everything else stays "noindex, nofollow".
// Belt-and-braces for crawlers that trust HTML meta tags over HTTP
// headers (Bing historically prefers meta).
function applyRobotsMeta(route: Route): void {
  const indexable =
    route.kind === "landing" ||
    route.kind === "privacy" ||
    route.kind === "terms" ||
    route.kind === "guides-index" ||
    route.kind === "guide";
  const content = indexable ? "index, follow" : "noindex, nofollow";
  let tag = document.querySelector(
    'meta[name="robots"]',
  ) as HTMLMetaElement | null;
  if (!tag) {
    tag = document.createElement("meta");
    tag.name = "robots";
    document.head.appendChild(tag);
  }
  tag.content = content;
}

export default function App() {
  const route = useRoute();

  // Appearance settings applied on mount so ANY route (landing, legal pages,
  // shared view) uses the user's chosen theme.
  useEffect(() => {
    applyAppearance(getSettings());
  }, []);

  // Keep the robots meta tag in sync with the current route. Server-side
  // X-Robots-Tag is the primary control; this covers JS-rendering
  // crawlers that read the meta after the SPA hydrates.
  useEffect(() => {
    applyRobotsMeta(route);
  }, [route]);

  // Public share view: standalone — no auth, no sidebar, no app chrome.
  if (route.kind === "share") {
    return <PublicShareView token={route.token} />;
  }
  // /privacy, /terms, /changelog, and the SEO guides stay fully public —
  // no auth wrapping.
  if (route.kind === "privacy") return <PrivacyPage />;
  if (route.kind === "terms") return <TermsPage />;
  if (route.kind === "changelog") return <ChangelogPage />;
  if (route.kind === "guides-index") return <GuidesIndexPage />;
  if (route.kind === "guide") return <GuidePage slug={route.slug} />;

  // Landing route: signed-out visitors get the marketing page, signed-in
  // visitors get the app rendered in place. Rendering AuthenticatedApp
  // directly (rather than redirecting to "/") avoids an infinite loop on
  // the marketing host casethread.app, where "/" ALSO resolves to the
  // landing route — a redirect there would just bounce back to this
  // block. Signed-in users on casethread.app see the full app inline;
  // the URL can stay as /welcome, which is fine since the routing is
  // now driven by auth state rather than pathname on this branch.
  if (route.kind === "landing") {
    return (
      <>
        <SignedIn>
          <AuthenticatedApp />
        </SignedIn>
        <SignedOut>
          <LandingPage />
        </SignedOut>
      </>
    );
  }

  // Fall-through: the authenticated app at /. Signed-out users get
  // redirected to the marketing landing page instead of a blank /
  // render that would immediately fail API calls with 401.
  return (
    <>
      <SignedIn>
        <AuthenticatedApp />
      </SignedIn>
      <SignedOut>
        <RedirectTo to="/welcome" />
      </SignedOut>
    </>
  );
}

// Small SPA redirect helper: mounts, pushes a new history entry, and
// dispatches popstate so the existing useRoute() listener picks up the
// change. Keyed on `to` so switching targets re-fires the effect; a
// dependency on pathname is deliberately NOT added — the effect should
// only redirect once per mount.
function RedirectTo({ to }: { to: string }) {
  useEffect(() => {
    if (window.location.pathname === to) return;
    window.history.pushState({}, "", to);
    window.dispatchEvent(new PopStateEvent("popstate"));
  }, [to]);
  return null;
}

function AuthenticatedApp() {
  const settings = useSettings();
  const [cases, setCases] = useState<Case[]>([]);
  const [view, setView] = useState<View>({ kind: "empty" });
  const [userEmail, setUserEmail] = useState<string | null>(null);
  const [displayName, setDisplayName] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(loadSidebarCollapsed);
  const toggleSidebarCollapsed = useCallback(() => {
    setSidebarCollapsed((v) => {
      const next = !v;
      try {
        window.localStorage.setItem(SIDEBAR_COLLAPSED_KEY, next ? "1" : "0");
      } catch {
        /* storage blocked — collapse just won't persist */
      }
      return next;
    });
  }, []);
  // Subscription state — populated from /api/me, re-fetched after a
  // successful checkout return so the trial banner clears without a
  // full page reload.
  const [plan, setPlan] = useState<string>("trial");
  const [daysRemaining, setDaysRemaining] = useState<number | null>(null);
  const [hasSubscription, setHasSubscription] = useState(false);
  const [cancelAtPeriodEnd, setCancelAtPeriodEnd] = useState(false);
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<string | null>(
    null,
  );
  const [checkoutSuccess, setCheckoutSuccess] = useState(false);
  // AI usage meter — fed to the Sidebar compact indicator. Refreshed
  // on mount, every 60s, on window focus, and imperatively via
  // setAiUsageListener() so AI-heavy components can nudge it right
  // after a response lands.
  const [aiMeter, setAiMeter] = useState<AiUsageMeter | null>(null);
  // Which Settings section to scroll to when the dialog opens. Cleared
  // once Settings has consumed it so subsequent opens land at the top.
  const [settingsInitialSection, setSettingsInitialSection] = useState<
    "subscription" | null
  >(null);
  // Volume state for the currently-open case. Lifted up from CaseView
  // so the Sidebar (also owned by this component) can render the
  // volume tree + kebab menu under the active case row. CaseView
  // still runs the fetch; it just mirrors the result here via
  // onVolumesChanged.
  const [activeVolumes, setActiveVolumes] = useState<CaseVolume[]>([]);
  // State declaratively passed through CaseView → Timeline. When
  // non-null, Timeline force-expands that volume's section, scrolls
  // the summary into view, and calls back via onShowSummaryHandled
  // to clear it. Lifted from CaseView so it has a single owner
  // (App) and flows as a controlled prop.
  const [showSummaryVolumeId, setShowSummaryVolumeId] = useState<
    string | null
  >(null);
  // Imperative handles registered by the mounted CaseView. Used for
  // actions that need to reach CaseView-owned state (tab, Interrogate
  // scope) but that don't warrant lifting that state to App.
  //   caseRefreshRef  — refetch records + volumes after a sidebar
  //                     action (close / rename).
  //   caseSetTabRef   — flip CaseView's active tab.
  //   caseSetScopeRef — set the Interrogate scope ("case" or a volume
  //                     id) for the "Ask AI about this volume" flow.
  const caseRefreshRef = useRef<(() => Promise<void>) | null>(null);
  const caseSetTabRef = useRef<((tab: CaseViewTab) => void) | null>(null);
  const caseSetScopeRef = useRef<
    ((scope: InterrogateScope) => void) | null
  >(null);

  const activeCaseId = view.kind === "case" ? view.id : null;
  // Clear volume-related state when the active case changes; we're
  // now looking at a different case's volumes.
  useEffect(() => {
    setActiveVolumes([]);
    setShowSummaryVolumeId(null);
  }, [activeCaseId]);

  // Row click on a volume in the sidebar tree. Lightweight — just
  // bring the user to the Records tab. Deliberately NOT the summary
  // opener; that's onViewVolumeSummary below.
  const handleSidebarSelectVolume = useCallback((_volumeId: string) => {
    caseSetTabRef.current?.("records");
  }, []);

  // Kebab "View summary" — switch to Records and ask Timeline to
  // force-expand that volume's section + scroll the summary into
  // view. Distinct from the row-click handler so the two intents
  // (browse records / read summary) don't collide on the same code
  // path. State flow: App sets showSummaryVolumeId → CaseView
  // forwards it to Timeline → Timeline scrolls + calls
  // onShowSummaryHandled to clear it back to null.
  const handleViewVolumeSummary = useCallback((volumeId: string) => {
    caseSetTabRef.current?.("records");
    setShowSummaryVolumeId(volumeId);
  }, []);

  // "Ask AI about this volume" in the sidebar kebab: flip to the
  // Interrogate tab and set the scope to that volume. Interrogate's
  // scope selector drives everything from there (fresh chat, system
  // message, drill-back endpoint dispatch).
  const handleSidebarAskAiAboutVolume = useCallback((volumeId: string) => {
    caseSetTabRef.current?.("interrogate");
    caseSetScopeRef.current?.(volumeId);
  }, []);

  const handleSidebarCloseCurrentVolume = useCallback(async () => {
    if (!activeCaseId) return;
    try {
      await api.closeVolume(activeCaseId);
      await caseRefreshRef.current?.();
    } catch (err) {
      // Surface via the existing app-level error banner rather than
      // inventing a new toast location.
      setError((err as Error).message);
    }
  }, [activeCaseId]);

  const handleSidebarRenameVolume = useCallback(
    async (volumeId: string, title: string) => {
      if (!activeCaseId) return;
      try {
        await api.updateVolumeTitle(activeCaseId, volumeId, title);
        await caseRefreshRef.current?.();
      } catch (err) {
        setError((err as Error).message);
      }
    },
    [activeCaseId],
  );

  const refreshCases = useCallback(async () => {
    try {
      const { cases } = await api.listCases();
      setCases(cases);
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  const refreshAiMeter = useCallback(async () => {
    try {
      const m = await api.aiUsageMeter();
      setAiMeter(m);
    } catch (err) {
      // Meter is a status indicator; a transient failure shouldn't
      // blow up the app. Keep the previous value until the next poll.
      console.warn("ai usage meter fetch failed:", (err as Error).message);
    }
  }, []);

  // Mount: fetch once, register the cross-component refresh signal,
  // set up a 60s poll and a window-focus refresh. Polling plus focus
  // covers AI calls we don't explicitly hook (SummaryCard regen,
  // KeyFactsPanel, Story, CostsPanel, etc.) — the explicit signal
  // hooks in Interrogate et al. just make the common cases feel
  // live.
  useEffect(() => {
    void refreshAiMeter();
    setAiUsageListener(() => {
      void refreshAiMeter();
    });
    const id = window.setInterval(() => void refreshAiMeter(), 60_000);
    const onFocus = () => void refreshAiMeter();
    window.addEventListener("focus", onFocus);
    return () => {
      setAiUsageListener(null);
      window.clearInterval(id);
      window.removeEventListener("focus", onFocus);
    };
  }, [refreshAiMeter]);

  // Refresh profile + cases. Exposed as a helper so the checkout-
  // return effect can re-run it without duplicating the setup code.
  const loadProfile = useCallback(async () => {
    try {
      const [{ user }, { cases }] = await Promise.all([
        api.me(),
        api.listCases(),
      ]);
      setUserEmail(user.email);
      setDisplayName(user.display_name);
      setCases(cases);
      setPlan(user.plan);
      setDaysRemaining(user.days_remaining);
      setHasSubscription(user.has_subscription);
      setCancelAtPeriodEnd(user.cancel_at_period_end);
      setCurrentPeriodEnd(user.current_period_end);
      updateSettings({
        records: { ...getSettings().records, retention: user.retention },
        notifications: {
          emailOnForward: user.notify_on_forward,
          weeklySummary: user.notify_weekly_summary,
        },
        account: {
          ...getSettings().account,
          displayName: user.display_name ?? "",
        },
      });
    } catch (err) {
      setError((err as Error).message);
    }
  }, []);

  useEffect(() => {
    void loadProfile();
  }, [loadProfile]);

  // Handle Stripe Checkout return. `?checkout=success` → user just
  // completed payment; refetch the profile so the plan flips from
  // 'trial' to 'active' and the trial banner disappears. Also pop a
  // one-shot success banner. `?checkout=cancelled` → just clean the
  // URL, no message. In both cases we replaceState so the checkout
  // query param can't reappear on a page refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const checkout = params.get("checkout");
    if (!checkout) return;
    if (checkout === "success") {
      setCheckoutSuccess(true);
      void loadProfile();
    }
    window.history.replaceState({}, "", window.location.pathname);
  }, [loadProfile]);

  // Listen for "open onboarding" events fired from Settings' Replay tour.
  useEffect(() => {
    function open() {
      setOnboardingOpen(true);
    }
    window.addEventListener("casethread:open-onboarding", open);
    return () =>
      window.removeEventListener("casethread:open-onboarding", open);
  }, []);

  const handleCaseCreated = useCallback((created: Case) => {
    setCases((prev) => [created, ...prev]);
    setView({ kind: "case", id: created.id });
    // First-ever case: trigger onboarding unless the user has already seen it.
    try {
      if (window.localStorage.getItem(ONBOARDING_KEY) !== "1") {
        setOnboardingOpen(true);
      }
    } catch {
      /* localStorage blocked — ignore */
    }
  }, []);

  const handleCaseDeleted = useCallback(async (id: string) => {
    setCases((prev) => prev.filter((c) => c.id !== id));
    setView({ kind: "empty" });
  }, []);

  const handleCaseUpdated = useCallback((next: Case) => {
    setCases((prev) => prev.map((c) => (c.id === next.id ? next : c)));
  }, []);

  // Drag-drop assign from the sidebar. Optimistic: patch the local
  // cases[] first so the UI reflects the move instantly, then send
  // the PATCH and reconcile with the server's reply. On failure,
  // roll back to the previous group so the case reappears where it
  // came from.
  const handleAssignCaseToGroup = useCallback(
    async (caseId: string, groupName: string | null) => {
      let previous: string | null | undefined;
      setCases((prev) =>
        prev.map((c) => {
          if (c.id !== caseId) return c;
          previous = c.group_name;
          return { ...c, group_name: groupName };
        }),
      );
      try {
        const { case: next } = await api.updateCase(caseId, {
          group_name: groupName,
        });
        setCases((prev) => prev.map((c) => (c.id === next.id ? next : c)));
      } catch (err) {
        console.error("group assign failed, rolling back:", err);
        setCases((prev) =>
          prev.map((c) =>
            c.id === caseId ? { ...c, group_name: previous ?? null } : c,
          ),
        );
      }
    },
    [],
  );

  // Delete group = unset group_name on every member. Issued in
  // parallel but the final setCases(refreshed) makes the list
  // consistent regardless of per-request ordering.
  const handleDeleteGroup = useCallback(
    async (groupName: string) => {
      const members = cases.filter(
        (c) => (c.group_name ?? null) === groupName,
      );
      if (members.length === 0) return;
      // Optimistic clear.
      setCases((prev) =>
        prev.map((c) =>
          (c.group_name ?? null) === groupName
            ? { ...c, group_name: null }
            : c,
        ),
      );
      try {
        await Promise.all(
          members.map((m) =>
            api.updateCase(m.id, { group_name: null }),
          ),
        );
      } catch (err) {
        console.error(
          "group delete partial failure, refreshing from server:",
          err,
        );
        // Reconcile with authoritative server state.
        void refreshCases();
      }
    },
    [cases, refreshCases],
  );

  const handleAccountDeleted = useCallback(() => {
    window.location.reload();
  }, []);

  function closeOnboarding() {
    setOnboardingOpen(false);
    try {
      window.localStorage.setItem(ONBOARDING_KEY, "1");
    } catch {}
  }

  function navigateCase(id: string) {
    setView({ kind: "case", id });
  }

  const forwardingAddress =
    cases.length === 1
      ? cases[0]!.forward_address
      : cases.length > 1
        ? `${cases.length} addresses (one per case)`
        : null;

  // Distinct folder names across the user's cases, alphabetised.
  // Passed straight to NewCaseForm so the form renders instantly
  // without a round-trip to /api/cases/groups (and, by extension,
  // without a flicker between "no folder field" and "folder
  // dropdown" on first paint).
  const folderNames = useMemo(() => {
    const set = new Set<string>();
    for (const c of cases) {
      const g = c.group_name?.trim();
      if (g) set.add(g);
    }
    return Array.from(set).sort((a, b) =>
      a.localeCompare(b, undefined, { sensitivity: "base" }),
    );
  }, [cases]);

  const selectCase = useCallback((id: string) => {
    setView({ kind: "case", id });
    setSidebarOpen(false);
  }, []);
  // Default folder for the "+ New case" flow: if the user currently
  // has a case selected that's inside a folder, inherit that folder
  // as the default pre-selection. Keeps creation flow in-context.
  const newCase = useCallback(() => {
    const activeFolder = activeCaseId
      ? (cases.find((c) => c.id === activeCaseId)?.group_name ?? null)
      : null;
    setView({
      kind: "new",
      presetFolder: activeFolder,
      folderLocked: false,
    });
    setSidebarOpen(false);
  }, [activeCaseId, cases]);
  // Entry point from the folder row's hover "+" icon. The folder is
  // already the user's declared context, so the dropdown in the form
  // is locked — they can't drift a new case into a different folder
  // without cancelling and re-entering.
  const newCaseInFolder = useCallback((folderName: string) => {
    setView({
      kind: "new",
      presetFolder: folderName,
      folderLocked: true,
    });
    setSidebarOpen(false);
  }, []);

  return (
    <div
      className={`app${sidebarOpen ? " sidebar-open" : ""}${
        sidebarCollapsed ? " sidebar-collapsed" : ""
      }`}
    >
      <button
        type="button"
        className="mobile-menu-btn"
        onClick={() => setSidebarOpen(true)}
        aria-label="Open navigation"
        aria-expanded={sidebarOpen}
      >
        <span aria-hidden>☰</span>
      </button>
      {/* Desktop-only: expand the collapsed nav rail. Hidden when expanded
          (the collapse chevron lives in the sidebar header). */}
      <button
        type="button"
        className="sidebar-expand-btn"
        onClick={toggleSidebarCollapsed}
        aria-label="Expand navigation"
        title="Expand navigation"
      >
        <span aria-hidden>»</span>
      </button>
      <Sidebar
        onToggleCollapse={toggleSidebarCollapsed}
        cases={cases}
        activeId={activeCaseId}
        onSelect={selectCase}
        onNew={newCase}
        userEmail={displayName || userEmail}
        onOpenSettings={() => {
          setSettingsInitialSection(null);
          setSettingsOpen(true);
          setSidebarOpen(false);
        }}
        onOpenGuide={() => {
          setGuideOpen(true);
          setSidebarOpen(false);
        }}
        aiMeter={aiMeter}
        onOpenSubscription={() => {
          setSettingsInitialSection("subscription");
          setSettingsOpen(true);
          setSidebarOpen(false);
        }}
        mobileOpen={sidebarOpen}
        onCloseMobile={() => setSidebarOpen(false)}
        activeVolumes={activeVolumes}
        onSelectVolume={handleSidebarSelectVolume}
        onViewVolumeSummary={handleViewVolumeSummary}
        onAskAiAboutVolume={handleSidebarAskAiAboutVolume}
        onCloseCurrentVolume={handleSidebarCloseCurrentVolume}
        onRenameVolume={handleSidebarRenameVolume}
        onAssignCaseToGroup={handleAssignCaseToGroup}
        onDeleteGroup={handleDeleteGroup}
        onNewCaseInFolder={newCaseInFolder}
      />
      {sidebarOpen && (
        <div
          className="sidebar-backdrop"
          onClick={() => setSidebarOpen(false)}
          aria-hidden
        />
      )}
      <main className="main">
        <TrialBanner
          plan={plan}
          daysRemaining={daysRemaining}
          cancelAtPeriodEnd={cancelAtPeriodEnd}
          currentPeriodEnd={currentPeriodEnd}
          aiMeter={aiMeter}
          onOpenPlanAndBilling={() => {
            setSettingsInitialSection("subscription");
            setSettingsOpen(true);
          }}
        />
        {checkoutSuccess && (
          <div
            className="banner-success"
            role="status"
            onClick={() => setCheckoutSuccess(false)}
          >
            ✓ Welcome to CaseThread Personal! Your subscription is active.
          </div>
        )}
        {settings.ai.privacy && (
          <div className="banner-privacy">
            🔒 Privacy mode — all processing is local. Tasks that require
            cloud AI will return an error.
          </div>
        )}
        {error && <div className="banner-error">Error: {error}</div>}
        {view.kind === "empty" && (
          <div className="empty-state">
            <h1>CaseThread</h1>
            <p>
              Select a case on the left, or{" "}
              <button
                className="link-btn"
                onClick={newCase}
              >
                create a new case
              </button>
              .
            </p>
            <p className="muted">
              <a href="/welcome">About CaseThread</a> ·{" "}
              <a href="/privacy">Privacy</a> ·{" "}
              <a href="/terms">Terms</a>
            </p>
          </div>
        )}
        {view.kind === "new" && (
          <NewCaseForm
            onCreated={handleCaseCreated}
            onCancel={() => setView({ kind: "empty" })}
            folderNames={folderNames}
            initialFolder={view.presetFolder}
            folderLocked={view.folderLocked}
          />
        )}
        {view.kind === "case" && (
          <CaseView
            key={view.id}
            caseId={view.id}
            allCases={cases}
            onDeleted={handleCaseDeleted}
            onCaseChanged={refreshCases}
            onCaseUpdated={handleCaseUpdated}
            onNavigateCase={navigateCase}
            onVolumesChanged={setActiveVolumes}
            showSummaryVolumeId={showSummaryVolumeId}
            onShowSummaryHandled={() => setShowSummaryVolumeId(null)}
            onRegisterRefresh={(fn) => {
              caseRefreshRef.current = fn;
            }}
            onRegisterTabSetter={(fn) => {
              caseSetTabRef.current = fn;
            }}
            onRegisterSetInterrogateScope={(fn) => {
              caseSetScopeRef.current = fn;
            }}
          />
        )}
      </main>

      <Settings
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        cases={cases}
        userEmail={userEmail}
        forwardingAddress={forwardingAddress}
        onAccountDeleted={handleAccountDeleted}
        plan={plan}
        daysRemaining={daysRemaining}
        hasSubscription={hasSubscription}
        cancelAtPeriodEnd={cancelAtPeriodEnd}
        currentPeriodEnd={currentPeriodEnd}
        aiMeter={aiMeter}
        onBillingChanged={() => void loadProfile()}
        initialSection={settingsInitialSection}
        onDemoCaseLoaded={handleCaseCreated}
        onDemoCaseCleared={(id) => void handleCaseDeleted(id)}
      />

      <Guide
        open={guideOpen}
        onClose={() => setGuideOpen(false)}
        activeCaseForwardAddress={
          view.kind === "case"
            ? cases.find((c) => c.id === view.id)?.forward_address ?? null
            : null
        }
      />

      <Onboarding open={onboardingOpen} onClose={closeOnboarding} />
    </div>
  );
}
